/**
 * GlobeDebugPage - Debug page for testing the globe visualization.
 *
 * Provides two modes:
 * 1. Simple mode: Basic geometry test with manual curvature control
 * 2. Full mode: Tests GlobeCanvas with GlobeMapProvider
 *
 * The "concave globe" concept: camera inside a hemisphere looking at the interior surface.
 */

import { useState, useMemo, useRef, useEffect, Suspense } from 'react'
import { Canvas, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { GLOBE_CONFIG } from '@/domain/globe/constants'
import { MorphingHemisphere } from '@/components/globe/MorphingHemisphere'
import { TileMesh, TileMeshFallback } from '@/components/globe/TileMesh'
import { tileService } from '@/domain/globe/services/TileService'
import { getVisibleTiles } from '@/domain/globe/utils/tileCalculations'
import type { GlobeTile } from '@/domain/globe/types'

/**
 * Simple hemisphere geometry for debugging.
 * Creates a hemisphere where the camera (at origin) looks OUTWARD at the interior surface.
 *
 * Geometry layout (concave globe concept):
 * - Camera at origin looking forward (-Z direction)
 * - Hemisphere wraps around camera horizontally
 * - At v=0 (center), the point is directly in front at [0, 0, -radius]
 * - At v=1 (edge), points form a ring around camera at the horizon
 * - The interior surface is visible (like looking at a dome from inside)
 */
function DebugHemisphere({ curvature = 1 }: { curvature: number }) {
  const geometry = useMemo(() => {
    const radius = GLOBE_CONFIG.SPHERE_RADIUS
    const segments = 32

    const geo = new THREE.BufferGeometry()
    const positions: number[] = []
    const indices: number[] = []

    // Generate vertices for hemisphere
    // v=0: center point (directly in front of camera at -Z)
    // v=1: edge ring (horizon, wrapping around camera)
    for (let ring = 0; ring <= segments / 2; ring++) {
      for (let segment = 0; segment <= segments; segment++) {
        const u = segment / segments
        const v = ring / (segments / 2)

        // Azimuth angle (around the viewing axis, full circle)
        const theta = u * Math.PI * 2
        // Polar angle (from center/front to edge/horizon)
        const phi = v * (Math.PI / 2)

        // Spherical position - hemisphere in front of camera
        // phi=0: center point at [0, 0, -radius] (directly ahead)
        // phi=PI/2: ring at [x, y, 0] with x²+y²=radius² (horizon around camera)
        const sphereX = radius * Math.sin(phi) * Math.sin(theta)
        const sphereY = radius * Math.sin(phi) * Math.cos(theta)
        const sphereZ = -radius * Math.cos(phi) // Negative Z = in front of camera

        // Flat position (disk in front of camera, perpendicular to view)
        const flatR = v * radius
        const flatX = flatR * Math.sin(theta)
        const flatY = flatR * Math.cos(theta)
        const flatZ = -radius // Flat plane at z=-radius

        // Interpolate between curved and flat
        const px = sphereX * curvature + flatX * (1 - curvature)
        const py = sphereY * curvature + flatY * (1 - curvature)
        const pz = sphereZ * curvature + flatZ * (1 - curvature)

        positions.push(px, py, pz)
      }
    }

    // Generate indices (triangles)
    const vertsPerRow = segments + 1
    for (let ring = 0; ring < segments / 2; ring++) {
      for (let segment = 0; segment < segments; segment++) {
        const a = ring * vertsPerRow + segment
        const b = a + vertsPerRow
        const c = a + 1
        const d = b + 1

        // Two triangles per quad (winding for interior visibility)
        indices.push(a, c, b)
        indices.push(c, d, b)
      }
    }

    geo.setIndex(indices)
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
    geo.computeVertexNormals()

    return geo
  }, [curvature])

  return (
    <mesh geometry={geometry}>
      <meshBasicMaterial color={0x4488ff} side={THREE.DoubleSide} wireframe />
    </mesh>
  )
}

/**
 * Camera controller - sets initial camera orientation.
 */
function CameraSetup({ fov }: { fov?: number }) {
  const { camera } = useThree()
  const initialized = useRef(false)

  useEffect(() => {
    if (!initialized.current) {
      // Point camera forward at the hemisphere center (default -Z direction)
      camera.lookAt(0, 0, -GLOBE_CONFIG.SPHERE_RADIUS)
      initialized.current = true
    }
  }, [camera])

  // Update FOV dynamically
  useEffect(() => {
    if (fov !== undefined && camera instanceof THREE.PerspectiveCamera) {
      camera.fov = fov
      camera.updateProjectionMatrix()
    }
  }, [camera, fov])

  return null
}

/**
 * Simple mode - basic geometry test with manual curvature control.
 */
function SimpleMode() {
  const [curvature, setCurvature] = useState(1)

  return (
    <div className="w-full h-screen bg-gray-900 relative">
      <Canvas
        camera={{
          position: [0, 0, 0],
          fov: 90,
          near: 0.1,
          far: 100,
        }}
      >
        <ambientLight intensity={1} />
        <CameraSetup />
        <DebugHemisphere curvature={curvature} />
        {/* Reference axes for debugging */}
        <axesHelper args={[5]} />
      </Canvas>

      {/* Controls */}
      <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-md shadow-lg p-4 w-64">
        <h2 className="font-bold text-lg mb-4">Simple Mode</h2>
        <div className="space-y-2">
          <label className="text-sm font-medium block">
            Curvature: {curvature.toFixed(2)}
          </label>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={curvature}
            onChange={(e) => setCurvature(Number(e.target.value))}
            className="w-full"
          />
        </div>
        <p className="text-xs text-gray-500 mt-4">
          1.0 = full hemisphere (curved)<br />
          0.0 = flat plane in front
        </p>
      </div>

      {/* Info */}
      <div className="absolute bottom-4 left-4 bg-black/70 text-white rounded-md p-3 text-sm">
        <p>Camera at origin, looking forward at hemisphere interior</p>
        <p className="text-xs text-gray-400 mt-1">
          Radius: {GLOBE_CONFIG.SPHERE_RADIUS}, Segments: 32
        </p>
        <p className="text-xs text-gray-400">
          Axes: Red=X, Green=Y, Blue=Z
        </p>
      </div>
    </div>
  )
}

/**
 * Full mode - tests GlobeCanvas with GlobeMapProvider.
 * Uses StandaloneGlobeCanvas first to isolate issues.
 */
function FullMode() {
  const [curvature, setCurvature] = useState(1)
  const [fov, setFov] = useState(90)

  return (
    <div className="w-full h-screen bg-gray-900 relative">
      <Canvas
        camera={{
          position: [0, 0, 0],
          fov: fov,
          near: GLOBE_CONFIG.NEAR_CLIP,
          far: GLOBE_CONFIG.FAR_CLIP,
        }}
        gl={{
          antialias: true,
          alpha: false,
          powerPreference: 'high-performance',
        }}
      >
        <ambientLight intensity={1} />
        <CameraSetup fov={fov} />
        <MorphingHemisphere curvature={curvature} wireframe={true} />
      </Canvas>

      {/* Controls */}
      <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-md shadow-lg p-4 w-64">
        <h2 className="font-bold text-lg mb-4">Full Mode</h2>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium block">
              Curvature: {curvature.toFixed(2)}
            </label>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={curvature}
              onChange={(e) => setCurvature(Number(e.target.value))}
              className="w-full"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium block">
              FOV: {fov}°
            </label>
            <input
              type="range"
              min={20}
              max={120}
              step={1}
              value={fov}
              onChange={(e) => setFov(Number(e.target.value))}
              className="w-full"
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-4">
          Using MorphingHemisphere component
        </p>
      </div>

      {/* Info */}
      <div className="absolute bottom-4 left-4 bg-black/70 text-white rounded-md p-3 text-sm">
        <p>Testing MorphingHemisphere component</p>
        <p className="text-xs text-gray-400 mt-1">
          Same as Simple but using the actual component
        </p>
      </div>
    </div>
  )
}

/**
 * Tiles mode - tests map tiles on the hemisphere.
 */
function TilesMode() {
  const [curvature, setCurvature] = useState(1)
  const [zoom, setZoom] = useState(2)
  const [showDebugOverlay, setShowDebugOverlay] = useState(false)

  // Generate tiles that cover the visible area
  // FOV 90 means we can see a wide area - use getVisibleTiles to calculate
  const fov = 90
  const tiles = useMemo(() => {
    // Get all tiles visible from center (lat=0, lng=0) at given zoom and FOV
    const visibleCoords = getVisibleTiles(0, 0, fov, zoom)

    // Convert to GlobeTile objects
    return visibleCoords.map(coord => tileService.createTile(coord))
  }, [zoom])

  // Debug log when overlay is enabled
  useEffect(() => {
    if (showDebugOverlay) {
      console.log('[TilesMode] Debug mode enabled:', {
        zoom,
        curvature,
        tileCount: tiles.length,
        tiles: tiles.map(t => ({
          key: `${t.coord.z}/${t.coord.x}/${t.coord.y}`,
          bounds: t.bounds,
        })),
      })
    }
  }, [showDebugOverlay, zoom, curvature, tiles])

  return (
    <div className="w-full h-screen bg-gray-900 relative">
      <Canvas
        camera={{
          position: [0, 0, 0],
          fov: 90,
          near: GLOBE_CONFIG.NEAR_CLIP,
          far: GLOBE_CONFIG.FAR_CLIP,
        }}
        gl={{
          antialias: true,
          alpha: false,
          powerPreference: 'high-performance',
        }}
      >
        <ambientLight intensity={1} />
        <CameraSetup />
        <MorphingHemisphere curvature={curvature} wireframe={false} />

        {/* Render tiles */}
        {tiles.map((tile) => (
          <Suspense
            key={`${tile.coord.z}-${tile.coord.x}-${tile.coord.y}`}
            fallback={
              <TileMeshFallback
                tile={tile}
                curvature={curvature}
                radius={GLOBE_CONFIG.SPHERE_RADIUS}
                showDebugOverlay={showDebugOverlay}
              />
            }
          >
            <TileMesh
              tile={tile}
              curvature={curvature}
              radius={GLOBE_CONFIG.SPHERE_RADIUS}
              showDebugOverlay={showDebugOverlay}
            />
          </Suspense>
        ))}
      </Canvas>

      {/* Controls */}
      <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-md shadow-lg p-4 w-64">
        <h2 className="font-bold text-lg mb-4">Tiles Mode</h2>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium block">
              Curvature: {curvature.toFixed(2)}
            </label>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={curvature}
              onChange={(e) => setCurvature(Number(e.target.value))}
              className="w-full"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium block">
              Zoom: {zoom}
            </label>
            <input
              type="range"
              min={1}
              max={6}
              step={1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="debugOverlay"
              checked={showDebugOverlay}
              onChange={(e) => setShowDebugOverlay(e.target.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="debugOverlay" className="text-sm font-medium">
              Debug Overlay
            </label>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-4">
          Testing {tiles.length} tiles at zoom {zoom}
        </p>
        {showDebugOverlay && (
          <p className="text-xs text-blue-600 mt-2">
            Check browser console for tile debug logs
          </p>
        )}
      </div>

      {/* Info */}
      <div className="absolute bottom-4 left-4 bg-black/70 text-white rounded-md p-3 text-sm">
        <p>Testing map tiles on hemisphere</p>
        <p className="text-xs text-gray-400 mt-1">
          Tiles centered around lat=0, lng=0
        </p>
        {showDebugOverlay && (
          <div className="mt-2 pt-2 border-t border-gray-600">
            <p className="text-xs text-yellow-400">Debug overlay active</p>
            <p className="text-xs text-gray-400 mt-1">
              Red/Green lines = tile boundaries (odd/even)<br />
              Yellow labels = tile z/x/y coordinates
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Main debug page - provides mode switcher.
 */
export default function GlobeDebugPage() {
  const [mode, setMode] = useState<'simple' | 'full' | 'tiles'>('simple')

  return (
    <div className="w-full h-screen relative">
      {/* Mode switcher */}
      <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur-sm rounded-md shadow-lg p-2">
        <div className="flex gap-2">
          <button
            onClick={() => setMode('simple')}
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              mode === 'simple'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Simple
          </button>
          <button
            onClick={() => setMode('full')}
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              mode === 'full'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Full
          </button>
          <button
            onClick={() => setMode('tiles')}
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              mode === 'tiles'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Tiles
          </button>
        </div>
      </div>

      {/* Render selected mode */}
      {mode === 'simple' && <SimpleMode />}
      {mode === 'full' && <FullMode />}
      {mode === 'tiles' && <TilesMode />}
    </div>
  )
}
